import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { Client } from '@nsc/clients/entities/Client.js';
import { FieldExtractionKV, FieldExtractionResult } from '@nsc/field-extraction/entities/FieldExtractionResult.js';

export enum WorkflowStateType {
  INITIAL_FIELD_EXTRACTION = 'INITIAL_FIELD_EXTRACTION',
  INITIAL_FIELD_VALIDATION = 'INITIAL_FIELD_VALIDATION',
  NEED_FURTHER_UPDATE = 'NEED_FURTHER_UPDATE',
  DONE = 'DONE',
}

/**
 * One partner slot in the Verizon parallel-scheduling fan-out, in priority order (1 → 3).
 * `contactedAt` is null while the slot is queued and gets stamped when we actually email that
 * partner — that flip is what drives the staggered "add the next partner" logic. All slots that
 * have been contacted stay open simultaneously; the first one to return a valid, Tesco-approved
 * FE wins and the others are cancelled.
 */
@Entity()
export class SchedulingPartnerRequest extends AdornisEntity {
  static override _class = 'SchedulingPartnerRequest';

  static override resolveID(entity: any): string {
    return A.getGloballyUniqueID();
  }

  @Field(() => Int)
  partnerNumber: Maybe<1 | 2 | 3>;

  @Field(() => String)
  partnerName: Maybe<string>;

  @Field(() => String)
  partnerEmail: Maybe<string>;

  @Field(() => Date)
  contactedAt: Maybe<Date>;
}

@Entity()
export class WorkflowState extends MongoEntity {
  static override _class = 'WorkflowState';
  static override _collectionName = 'workflow-states';

  @DataLoadedField(() => Client, ({ clientId }) => clientId)
  client: Maybe<Client>;

  @Field(() => String)
  clientId: Maybe<string>;

  @Field(() => Date, { default: v => v ?? new Date() })
  createdAt: Maybe<Date>;

  @Field(() => Date, { default: v => v ?? new Date() })
  enrichmentAt: Maybe<Date>;

  @Field(() => Date, { default: v => v ?? new Date() })
  updatedAt: Maybe<Date>;

  @Field(() => String)
  type: Maybe<WorkflowStateType>;

  @Field(() => String)
  ticketID: Maybe<string>;

  @Field(() => Boolean)
  neededUpdate: Maybe<boolean>;

  @Field(() => FieldExtractionResult)
  initialExtraction: Maybe<FieldExtractionResult>;

  @Field(() => [FieldExtractionResult], { default: v => v ?? [] })
  updatedExtractions: Maybe<FieldExtractionResult[]>;

  @Field(() => Boolean, { default: v => v ?? false })
  dutyManagerCalled: Maybe<boolean>;

  @Field(() => Boolean, { default: v => v ?? false })
  dutyManagerOrMailboxAnswered: Maybe<boolean>;

  @Field(() => Boolean, { default: v => v ?? false })
  clientCalled: Maybe<boolean>;

  @Field(() => Boolean, { default: v => v ?? false })
  clientOrMailboxAnswered: Maybe<boolean>;

  @Field(() => String, { default: v => v ?? '' })
  error: Maybe<string>;

  @Field(() => [String])
  processedArticleIDs: Maybe<string[]>;

  // Verizon scheduling fields
  @Field(() => String)
  schedulingSubticketID: Maybe<string>;

  // Up-to-3 partner slots for the parallel fan-out, in priority order. Source of truth for which
  // partners exist, in what order, and which have been contacted (see SchedulingPartnerRequest).
  @Field(() => [SchedulingPartnerRequest], { default: v => v ?? [] })
  partnerRequests: Maybe<SchedulingPartnerRequest[]>;

  // Reply article IDs already acted on (declared a winner / sent feedback) so each partner reply is
  // handled exactly once across cron ticks.
  @Field(() => [String], { default: v => v ?? [] })
  schedulingHandledReplyArticleIDs: Maybe<string[]>;

  // --- Legacy single-partner scalar fields (pre parallel fan-out). Kept so existing staging rows
  // still deserialize; new scheduling runs use `partnerRequests` instead. ---
  @Field(() => Int)
  currentPartnerNumber: Maybe<1 | 2 | 3>;

  @Field(() => String)
  currentPartnerName: Maybe<string>;

  @Field(() => Date)
  partnerContactedAt: Maybe<Date>;

  @Field(() => String)
  currentPartnerEmail: Maybe<string>;

  @Field(() => String)
  schedulingStatus: Maybe<'pending' | 'awaiting_subticket_number' | 'partner_contacted' | 'completed' | 'stalled'>;

  @Field(() => Int)
  subticketNumberCheckAttempts: Maybe<number>;

  constructor(doc: PartialEntityData<WorkflowState>) {
    super(doc);
  }

  aggregatedExtractions() {
    const extractions = [this.initialExtraction, ...(this.updatedExtractions ?? [])].filter(
      Boolean,
    ) as FieldExtractionResult[];

    const allMissingFields = [...new Set(extractions.flatMap(extraction => extraction.missing ?? []))];
    const newestValues: Record<string, string> = {};
    for (const extraction of extractions) {
      for (const kv of extraction.values ?? []) {
        if (!kv.key || !kv.value || kv.value.trim() === '') continue;
        newestValues[kv.key] = kv.value;
      }
    }

    return new FieldExtractionResult({
      createdAt: new Date(),
      missing: allMissingFields.filter(key => newestValues[key] === 'null' || !newestValues[key]),
      values: Object.entries(newestValues).map(([key, value]) => new FieldExtractionKV({ key, value }).toJSON()),
    });
  }
}
